import React from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useHistory, useLocation } from 'react-router';

import { LearningInfoModal } from '@src/features/feed';
import { useUserData } from '@src/hooks/useUserData';
import { Flex, Text, Dropdown } from 'src/kit';
import { Loader } from 'src/components';
import { useJobFeed } from '../hooks/useJobFeed';
import LearningCard from '../molecules/LearningCard';
import { useScopeFilter } from '../hooks/useScopeFilter';

type FetchDataFunc = (qs: string) => string;

export const LearningList = observer(
  ({ getData, getTopPicksData }: { getData: FetchDataFunc; getTopPicksData: FetchDataFunc }) => {
    const [scope, setScope] = useScopeFilter('learningScope');
    const [user, isFetching] = useUserData();
    const { id } = useParams<{ id: any }>();
    const { ref, jobsList, isJobsFetched, error } = useJobFeed(getData, 'learningList');
    const {
      ref: topPicksRef,
      jobsList: topPicksList,
      isJobsFetched: isTopPicksFetched,
      error: topPicksError
    } = useJobFeed(getTopPicksData, 'learningTopPicksList', { customPath: 'top_pick_from_sainoo' });
    const history = useHistory();
    const { search } = useLocation();

    const isTopPicksListNonEmpty = isTopPicksFetched && topPicksList.length > 0;

    const scopeFilter = (
      <Flex pb={42} height={82} flex="1" justifyContent="flex-end">
        <Flex flex="1" alignItems="flex-end">
          <Text fontSize="18px" style={{ height: 18 }} color="textPrimary">
            {isTopPicksListNonEmpty ? 'Top Picks from Sainoo' : 'Stay ahead of the curve!'}
          </Text>
        </Flex>
        {user && !isFetching && (
          <Dropdown
            initialSelectedItem={scope}
            items={[
              { value: 'all', label: 'All resources' },
              { value: 'suggested', label: 'Resources matching my profile' }
            ]}
            onSelectedItemChange={i => {
              setScope(i.selectedItem, 'learningScope');
            }}
          />
        )}
      </Flex>
    );

    const learningModal = id && (
      <LearningInfoModal
        learningId={Number(id)}
        onClose={() => {
          history.push({ pathname: '.', search });
        }}
      />
    );

    if (isJobsFetched && jobsList.length === 0) {
      return (
        <>
          {scopeFilter}
          {learningModal}
          <Flex flexWrap="wrap" pb={70} width="870px" justifyContent="center">
            <Text fontSize={16} fontWeight="light" color="textPrimary">
              No Items Found
            </Text>
          </Flex>
        </>
      );
    }

    return (
      <>
        {scopeFilter}
        {learningModal}

        <List dataList={topPicksList} innerRef={topPicksRef} />
        {!topPicksError && isTopPicksListNonEmpty && !isTopPicksFetched && (
          <Loader css={{ position: 'relative', height: '200px' }} />
        )}
        {isTopPicksListNonEmpty && (
          <Flex flex="1" alignItems="flex-end" pb={42}>
            <Text fontSize="18px" style={{ height: 18 }} color="textPrimary">
              More Great courses
            </Text>
          </Flex>
        )}
        <List dataList={jobsList} innerRef={ref} />

        {!error && !isJobsFetched && <Loader css={{ position: 'relative', height: '200px' }} />}
      </>
    );
  }
);

function List({ dataList, innerRef }) {
  const history = useHistory();
  const { search, pathname } = useLocation();
  return (
    <Flex
      flexWrap="wrap"
      pb={dataList.length > 0 ? 30 : 0}
      justifyContent="space-between"
      maxWidth={dataList.length === 0 ? '870px' : 'initial'}
    >
      {Boolean(dataList.length) && (
        <>
          {dataList.map(data => (
            <React.Fragment key={data.id}>
              <LearningCard
                data={data}
                onClick={() => {
                  history.push({
                    pathname: pathname + (pathname[pathname.length - 1] === '/' ? '' : '/') + data.id,
                    search
                  });
                }}
              />
            </React.Fragment>
          ))}
          {dataList.length > 1 && <div ref={innerRef} />}
        </>
      )}
    </Flex>
  );
}
